import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import AutoTranslate from "@/components/AutoTranslate";
import LanguagePrompt from "@/components/LanguagePrompt";
import LanguageHint from "@/components/LanguageHint";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { OfflineBanner } from "@/components/OfflineBanner";
import { DataSaverPrompt } from "@/components/DataSaverPrompt";
import { NotesProvider } from "@/context/NotesContext";
import { PinPromptProvider } from "@/context/PinPromptContext";
import { CookieConsentProvider } from "@/context/CookieConsentContext";

import { PublicLayout } from "@/components/PublicLayout";
import PortalLayout from "@/pages/portal/PortalLayout";
import AuthCallback from "@/pages/AuthCallback";
import { CallProvider } from "@/context/CallContext";
import { CallOverlay } from "@/components/CallOverlay";
import { GroupCallOverlay } from "@/components/GroupCallOverlay";
import { CallNotifyPrompt } from "@/components/CallNotifyPrompt";
import { WelcomeAlertsStep } from "@/components/WelcomeAlertsStep";
import { MissedCallBanner } from "@/components/MissedCallBanner";
import { InstallBanner } from "@/components/InstallBanner";
import RefCapture from "@/components/RefCapture";
import EnableAlertsPrompt from "@/components/EnableAlertsPrompt";
import RouteSeo from "@/components/RouteSeo";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { initErrorReporting } from "@/lib/errorReporter";

initErrorReporting();

// Lazy-loaded pages: each becomes its own chunk, so a first visit only downloads
// the page in view instead of the entire app (big win on weak / low-data networks).
const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const BlogAdmin = lazy(() => import("@/pages/portal/BlogAdmin"));
const Love = lazy(() => import("@/pages/Love"));
const LoveProfile = lazy(() => import("@/pages/LoveProfile"));
const LoveConnections = lazy(() => import("@/pages/LoveConnections"));
const Live = lazy(() => import("@/pages/Live"));
const Food = lazy(() => import("@/pages/Food"));
const FoodRestaurant = lazy(() => import("@/pages/FoodRestaurant"));
const MediaKit = lazy(() => import("@/pages/MediaKit"));
const Pitch = lazy(() => import("@/pages/Pitch"));
const LiveBroadcast = lazy(() => import("@/pages/LiveBroadcast"));
const LiveStudio = lazy(() => import("@/pages/LiveStudio"));
const PrayerWall = lazy(() => import("@/pages/PrayerWall"));
const PrayerInbox = lazy(() => import("@/pages/PrayerInbox"));
const Market = lazy(() => import("@/pages/Market"));
const FoundingPartners = lazy(() => import("@/pages/FoundingPartners"));
const Concierge = lazy(() => import("@/pages/Concierge"));
const Advertise = lazy(() => import("@/pages/Advertise"));
const MarketItem = lazy(() => import("@/pages/MarketItem"));
const MarketWishlist = lazy(() => import("@/pages/MarketWishlist"));
const MarketStore = lazy(() => import("@/pages/MarketStore"));
const MyStore = lazy(() => import("@/pages/MyStore"));
const BusinessLeads = lazy(() => import("@/pages/BusinessLeads"));
const MyEnquiries = lazy(() => import("@/pages/MyEnquiries"));
const MarketAgreement = lazy(() => import("@/pages/MarketAgreement"));
const MarketPrivacy = lazy(() => import("@/pages/MarketPrivacy"));
const MarketCookies = lazy(() => import("@/pages/MarketCookies"));
const Local = lazy(() => import("@/pages/Local"));
const Champions = lazy(() => import("@/pages/Champions"));
const Discover = lazy(() => import("@/pages/Discover"));
const WhyPula = lazy(() => import("@/pages/WhyPula"));
const ExplorePula = lazy(() => import("@/pages/ExplorePula"));
const CampusKit = lazy(() => import("@/pages/CampusKit"));
const Diaspora = lazy(() => import("@/pages/Diaspora"));
const SendHome = lazy(() => import("@/pages/SendHome"));
const OurPromise = lazy(() => import("@/pages/OurPromise"));
const CareEligibility = lazy(() => import("@/pages/CareEligibility"));
const PulaForBusiness = lazy(() => import("@/pages/PulaForBusiness"));
const VoiceTranslate = lazy(() => import("@/pages/Translate"));
const Makers = lazy(() => import("@/pages/Makers"));
const MakersEntry = lazy(() => import("@/pages/MakersEntry"));
const MakerEdit = lazy(() => import("@/pages/MakerEdit"));
const LocalCategory = lazy(() => import("@/pages/LocalCategory"));
const ForBusiness = lazy(() => import("@/pages/ForBusiness"));
const RepCheatSheet = lazy(() => import("@/pages/RepCheatSheet"));
const PulaConnect = lazy(() => import("@/pages/PulaConnect"));
const LocalBusiness = lazy(() => import("@/pages/LocalBusiness"));
const MyBusinessPage = lazy(() => import("@/pages/MyBusinessPage"));
const LocalRequests = lazy(() => import("@/pages/LocalRequests"));
const MyData = lazy(() => import("@/pages/MyData"));
const MyApplications = lazy(() => import("@/pages/MyApplications"));
const DayOne = lazy(() => import("@/pages/DayOne"));
const HowReviewsWork = lazy(() => import("@/pages/HowReviewsWork"));
const WhatsReal = lazy(() => import("@/pages/WhatsReal"));
const PromoteKit = lazy(() => import("@/pages/PromoteKit"));
const LocalGuides = lazy(() => import("@/pages/LocalGuides"));
const LocalGuide = lazy(() => import("@/pages/LocalGuide"));
const Properties = lazy(() => import("@/pages/Properties"));
const Stays = lazy(() => import("@/pages/Stays"));
const StayDetail = lazy(() => import("@/pages/StayDetail"));
const StaysHostGuide = lazy(() => import("@/pages/StaysHostGuide"));
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const CareJobsUK = lazy(() => import("@/pages/CareJobsUK"));
const ContentStudio = lazy(() => import("@/pages/ContentStudio"));
const CareTraining = lazy(() => import("@/pages/CareTraining"));
const ReadinessCertificate = lazy(() => import("@/pages/ReadinessCertificate"));
const LaunchPoster = lazy(() => import("@/pages/LaunchPoster"));
const PulaSearch = lazy(() => import("@/pages/PulaSearch"));
const News = lazy(() => import("@/pages/News"));
const MyAlerts = lazy(() => import("@/pages/MyAlerts"));
const WantedNear = lazy(() => import("@/pages/WantedNear"));
const Employers = lazy(() => import("@/pages/Employers"));
const JobDetail = lazy(() => import("@/pages/JobDetail"));
const Learn = lazy(() => import("@/pages/Learn"));
const Course = lazy(() => import("@/pages/Course"));
const LearnQuality = lazy(() => import("@/pages/LearnQuality"));
const ForEmployers = lazy(() => import("@/pages/ForEmployers"));
const Invite = lazy(() => import("@/pages/Invite"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const LearnCategory = lazy(() => import("@/pages/LearnCategory"));
const Talent = lazy(() => import("@/pages/Talent"));
const MyLearning = lazy(() => import("@/pages/MyLearning"));
const Verify = lazy(() => import("@/pages/Verify"));
const ComingSoon = lazy(() => import("@/pages/ComingSoon"));
const Services = lazy(() => import("@/pages/Services"));
const Pay = lazy(() => import("@/pages/Pay"));
const Wallet = lazy(() => import("@/pages/Wallet"));
const Search = lazy(() => import("@/pages/Search"));
const Safety = lazy(() => import("@/pages/Safety"));
const LoveSafetyHub = lazy(() => import("@/pages/love-safety/Hub"));
const SafetyGuides = lazy(() => import("@/pages/love-safety/SafetyGuides"));
const Transparency = lazy(() => import("@/pages/love-safety/Transparency"));
const Policies = lazy(() => import("@/pages/love-safety/Policies"));
const SafetyTools = lazy(() => import("@/pages/love-safety/SafetyTools"));
const DisputeResolution = lazy(() => import("@/pages/love-safety/DisputeResolution"));
const IdentityComplete = lazy(() => import("@/pages/love-safety/IdentityComplete"));
const Pledge = lazy(() => import("@/pages/Pledge"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const CodeOfConduct = lazy(() => import("@/pages/CodeOfConduct"));
const Contact = lazy(() => import("@/pages/Contact"));
const BusinessModel = lazy(() => import("@/pages/BusinessModel"));
const Mavula = lazy(() => import("@/pages/Mavula"));
const Why = lazy(() => import("@/pages/Why"));
const Vote = lazy(() => import("@/pages/Vote"));
const YourVoice = lazy(() => import("@/pages/YourVoice"));
const Creators = lazy(() => import("@/pages/Creators"));
const CreatorDashboard = lazy(() => import("@/pages/CreatorDashboard"));
const CreatorProfile = lazy(() => import("@/pages/CreatorProfile"));
const PublicPortfolio = lazy(() => import("@/pages/PublicPortfolio"));
const PulaLens = lazy(() => import("@/pages/PulaLens"));
const PulaMoments = lazy(() => import("@/pages/PulaMoments"));
const AskTheWise = lazy(() => import("@/pages/AskTheWise"));
const LegacyFund = lazy(() => import("@/pages/LegacyFund"));
const LensAbout = lazy(() => import("@/pages/LensAbout"));
const GetApp = lazy(() => import("@/pages/GetApp"));
const NotificationSettings = lazy(() => import("@/pages/NotificationSettings"));
const PulaChat = lazy(() => import("@/pages/PulaChat"));
const WiseQuestion = lazy(() => import("@/pages/WiseQuestion"));
const StoryView = lazy(() => import("@/pages/StoryView"));
const Saved = lazy(() => import("@/pages/Saved"));
const PulaTaxi = lazy(() => import("@/pages/PulaTaxi"));
const Messages = lazy(() => import("@/pages/Messages"));
const CommunityFinance = lazy(() => import("@/pages/CommunityFinance"));
const FamilyCircle = lazy(() => import("@/pages/FamilyCircle"));
const FamilyHub = lazy(() => import("@/pages/FamilyHub"));
const Neighbourhood = lazy(() => import("@/pages/Neighbourhood"));
const ForYou = lazy(() => import("@/pages/ForYou"));
const Business = lazy(() => import("@/pages/Business"));
const PayInvoice = lazy(() => import("@/pages/PayInvoice"));
const Students = lazy(() => import("@/pages/Students"));
const ForStudents = lazy(() => import("@/pages/ForStudents"));
const ForStudentsLeaflet = lazy(() => import("@/pages/ForStudentsLeaflet"));
const MemberProfile = lazy(() => import("@/pages/MemberProfile"));
const VerifyIdentity = lazy(() => import("@/pages/VerifyIdentity"));
const StudentMarketplace = lazy(() => import("@/pages/StudentMarketplace"));
const StudentGigs = lazy(() => import("@/pages/StudentGigs"));
const StudentVoice = lazy(() => import("@/pages/StudentVoice"));
const StudentExtras = lazy(() => import("@/pages/StudentExtras"));
const StudentStore = lazy(() => import("@/pages/StudentStore"));
const StudentOrders = lazy(() => import("@/pages/StudentOrders"));
const StudentGroupBuys = lazy(() => import("@/pages/StudentGroupBuys"));
const StudentMoveOut = lazy(() => import("@/pages/StudentMoveOut"));
const Curiosity = lazy(() => import("@/pages/Curiosity"));
const AssignmentReview = lazy(() => import("@/pages/AssignmentReview"));
const StudentDisputes = lazy(() => import("@/pages/portal/StudentDisputes"));
const StudentReports = lazy(() => import("@/pages/portal/StudentReports"));
const Impact = lazy(() => import("@/pages/Impact"));
const Livelihoods = lazy(() => import("@/pages/Livelihoods"));
const Community = lazy(() => import("@/pages/Community"));
const Connections = lazy(() => import("@/pages/Connections"));
const Locate = lazy(() => import("@/pages/Locate"));
const Calls = lazy(() => import("@/pages/Calls"));
const AboutMavula = lazy(() => import("@/pages/AboutMavula"));
const DiscussionPage = lazy(() => import("@/pages/DiscussionPage"));
const CVBuilder = lazy(() => import("@/pages/CVBuilder"));
const Auth = lazy(() => import("@/pages/Auth"));
const PaymentSuccess = lazy(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentCancel })));
const Ambassador = lazy(() => import("@/pages/Ambassador"));
const AmbassadorShowcase = lazy(() => import("@/pages/AmbassadorShowcase"));
const AmbassadorsAdmin = lazy(() => import("@/pages/portal/AmbassadorsAdmin"));
const JobsModeration = lazy(() => import("@/pages/portal/JobsModeration"));
const LensModeration = lazy(() => import("@/pages/portal/LensModeration"));
const LegacyFundReview = lazy(() => import("@/pages/portal/LegacyFundReview"));
const PortalAnalytics = lazy(() => import("@/pages/portal/Analytics"));
const PortalMarketing = lazy(() => import("@/pages/portal/Marketing"));
const PortalBroadcastChannels = lazy(() => import("@/pages/portal/BroadcastChannels"));
const PortalReengage = lazy(() => import("@/pages/portal/Reengage"));
const LeadStudio = lazy(() => import("@/pages/portal/LeadStudio"));
const AITeam = lazy(() => import("@/pages/portal/AITeam"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const Proposals = lazy(() => import("@/pages/portal/Proposals"));
const ProposalView = lazy(() => import("@/pages/ProposalView"));
const ExecOverview = lazy(() => import("@/pages/portal/ExecOverview"));
const BusinessPlan = lazy(() => import("@/pages/portal/BusinessPlan"));
const PropertyPlan = lazy(() => import("@/pages/portal/PropertyPlan"));
const LiveModeration = lazy(() => import("@/pages/portal/LiveModeration"));
const Team = lazy(() => import("@/pages/portal/Team"));
const Security = lazy(() => import("@/pages/portal/Security"));
const Rewards = lazy(() => import("@/pages/portal/Rewards"));
const Translate = lazy(() => import("@/pages/portal/Translate"));
const SafetyTranslations = lazy(() => import("@/pages/portal/SafetyTranslations"));
const FieldMode = lazy(() => import("@/pages/portal/FieldMode"));
const Onboarding = lazy(() => import("@/pages/portal/Onboarding"));
const ReferralLeaderboard = lazy(() => import("@/pages/portal/ReferralLeaderboard"));
const Enquiries = lazy(() => import("@/pages/portal/Enquiries"));
const CopilotStudio = lazy(() => import("@/pages/portal/CopilotStudio"));
const ReceptionLogs = lazy(() => import("@/pages/portal/ReceptionLogs"));
const WaitlistDigest = lazy(() => import("@/pages/portal/WaitlistDigest"));
const LaunchRsvps = lazy(() => import("@/pages/portal/LaunchRsvps"));
const StaffActivity = lazy(() => import("@/pages/portal/StaffActivity"));
const ChecklistLaunch = lazy(() => import("@/pages/portal/ChecklistLaunch"));
const RecapLaunch = lazy(() => import("@/pages/portal/RecapLaunch"));
const SignupHelper = lazy(() => import("@/pages/portal/SignupHelper"));
const LaunchTicker = lazy(() => import("@/pages/LaunchTicker"));
const LaunchCards = lazy(() => import("@/pages/LaunchCards"));
const RunSheet = lazy(() => import("@/pages/RunSheet"));
const QrPoster = lazy(() => import("@/pages/QrPoster"));
const QrTableTent = lazy(() => import("@/pages/QrTableTent"));
const LaunchKit = lazy(() => import("@/pages/LaunchKit"));
const RsvpLaunch = lazy(() => import("@/pages/RsvpLaunch"));
const LaunchFeedback = lazy(() => import("@/pages/LaunchFeedback"));
const Trust = lazy(() => import("@/pages/Trust"));
const HowWeMakeMoney = lazy(() => import("@/pages/HowWeMakeMoney"));
const LiveSafetyPage = lazy(() => import("@/pages/LiveSafetyPage"));
const Referrals = lazy(() => import("@/pages/Referrals"));
const Appointments = lazy(() => import("@/pages/Appointments"));
const Confidante = lazy(() => import("@/pages/Confidante"));
const PortalLogin = lazy(() => import("@/pages/portal/PortalLogin"));
const Dashboard = lazy(() => import("@/pages/portal/Dashboard"));
const Clients = lazy(() => import("@/pages/portal/Clients"));
const Cases = lazy(() => import("@/pages/portal/Cases"));
const Staff = lazy(() => import("@/pages/portal/Staff"));
const Audit = lazy(() => import("@/pages/portal/Audit"));
const Resources = lazy(() => import("@/pages/portal/Resources"));
const Voices = lazy(() => import("@/pages/portal/Voices"));
const Verifications = lazy(() => import("@/pages/portal/Verifications"));
const DiscussionsMod = lazy(() => import("@/pages/portal/DiscussionsMod"));
const HappeningsMod = lazy(() => import("@/pages/portal/HappeningsMod"));
const CommunityMods = lazy(() => import("@/pages/portal/CommunityMods"));
const MarketReports = lazy(() => import("@/pages/portal/MarketReports"));
const MarketSearchAdmin = lazy(() => import("@/pages/portal/MarketSearchAdmin"));
const MakerVerifications = lazy(() => import("@/pages/portal/MakerVerifications"));
const LocalBusinessesAdmin = lazy(() => import("@/pages/portal/LocalBusinessesAdmin"));
const AdsAdmin = lazy(() => import("@/pages/portal/AdsAdmin"));
const SafetyConcerns = lazy(() => import("@/pages/portal/SafetyConcerns"));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
      <div className="w-8 h-8 rounded-full border-2 border-pula-green/30 border-t-pula-green animate-spin" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Always start a newly-opened page at the top (unless linking to an in-page #anchor).
    if (!window.location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Emergent Google OAuth returns to /auth/callback#session_id=...
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <RefCapture />
      <RouteSeo />
      <EnableAlertsPrompt />
      <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/love" element={<Love />} />
        <Route path="/love/profile/:id" element={<LoveProfile />} />
        <Route path="/love/connections" element={<LoveConnections />} />
        <Route path="/live" element={<Live />} />
        <Route path="/live/:id" element={<LiveBroadcast />} />
        <Route path="/live/studio/:id" element={<LiveStudio />} />
        <Route path="/food" element={<Food />} />
        <Route path="/food/:id" element={<FoodRestaurant />} />
        <Route path="/media-kit" element={<MediaKit />} />
        <Route path="/pitch" element={<Pitch />} />
        <Route path="/prayers" element={<PrayerWall />} />
        <Route path="/prayers/inbox" element={<PrayerInbox />} />
        <Route path="/market" element={<Market />} />
        <Route path="/founding-partners" element={<FoundingPartners />} />
        <Route path="/concierge" element={<Concierge />} />        <Route path="/advertise" element={<Advertise />} />
        <Route path="/services" element={<Services />} />

        <Route path="/market/wishlist" element={<MarketWishlist />} />
        <Route path="/market/seller/:id" element={<MarketStore />} />
        <Route path="/market/my-store" element={<MyStore />} />
        <Route path="/business/leads" element={<BusinessLeads />} />
        <Route path="/market/enquiries" element={<MyEnquiries />} />
        <Route path="/market/agreement" element={<MarketAgreement />} />
        <Route path="/market/privacy" element={<MarketPrivacy />} />
        <Route path="/market/cookies" element={<MarketCookies />} />
        <Route path="/local" element={<Local />} />
        <Route path="/local/how-reviews-work" element={<HowReviewsWork />} />
        <Route path="/whats-real" element={<WhatsReal />} />
        <Route path="/local/guides" element={<LocalGuides />} />
        <Route path="/local/guides/:slug" element={<LocalGuide />} />
        <Route path="/promote" element={<PromoteKit />} />
        <Route path="/champions" element={<Champions />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/why-pula" element={<WhyPula />} />
        <Route path="/explore" element={<ExplorePula />} />
        <Route path="/campus" element={<CampusKit />} />
        <Route path="/diaspora" element={<Diaspora />} />
        <Route path="/send-home" element={<SendHome />} />
        <Route path="/our-promise" element={<OurPromise />} />
        <Route path="/pula-for-business" element={<PulaForBusiness />} />
        <Route path="/translate" element={<VoiceTranslate />} />
        <Route path="/makers" element={<Makers />} />
        <Route path="/makers/me/edit" element={<MakerEdit />} />
        <Route path="/makers/:id" element={<MakersEntry />} />
        <Route path="/local/category/:category" element={<LocalCategory />} />
        <Route path="/local/town/:town" element={<LocalCategory />} />
        <Route path="/local/category/:category/town/:town" element={<LocalCategory />} />
        <Route path="/local/requests" element={<LocalRequests />} />
        <Route path="/local/my-business" element={<MyBusinessPage />} />
        <Route path="/for-business" element={<ForBusiness />} />
        <Route path="/rep-card" element={<RepCheatSheet />} />
        <Route path="/connect" element={<PulaConnect />} />        <Route path="/local/:id" element={<LocalBusiness />} />
        <Route path="/account/data" element={<MyData />} />
        <Route path="/market/:id" element={<MarketItem />} />
        <Route path="/properties" element={<Properties />} />
        <Route path="/stays" element={<Stays />} />
        <Route path="/stays/:id" element={<StayDetail />} />
        <Route path="/stays/host-guide" element={<StaysHostGuide />} />
        <Route path="/properties/:id" element={<PropertyDetail />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/care-jobs-uk" element={<CareJobsUK />} />
        <Route path="/content-studio" element={<ContentStudio />} />
        <Route path="/care-eligibility" element={<CareEligibility />} />
        <Route path="/care-training" element={<CareTraining />} />        <Route path="/launch-poster" element={<LaunchPoster />} />
        <Route path="/search" element={<PulaSearch />} />
        <Route path="/news" element={<News />} />
        <Route path="/my-alerts" element={<MyAlerts />} />
        <Route path="/wanted/:town" element={<WantedNear />} />
        <Route path="/employers" element={<Employers />} />
        <Route path="/for-employers" element={<ForEmployers />} />
        <Route path="/invite" element={<Invite />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/post-a-job" element={<Employers />} />
        <Route path="/business/dashboard" element={<Employers />} />
        <Route path="/jobs/my-applications" element={<MyApplications />} />
        <Route path="/day-one/:applicationId" element={<DayOne />} />
        <Route path="/jobs/:id" element={<JobDetail />} />
        <Route path="/learn" element={<Learn />} />
        <Route path="/learn/category/:category" element={<LearnCategory />} />
        <Route path="/talent" element={<Talent />} />
        <Route path="/learn/:courseId" element={<Course />} />
        <Route path="/learn-quality" element={<LearnQuality />} />
        <Route path="/my-learning" element={<MyLearning />} />
        <Route path="/verify/:id" element={<Verify />} />
        <Route path="/coming-soon/:feature" element={<ComingSoon />} />
        <Route path="/pay/:id" element={<Pay />} />


        <Route path="/wallet" element={<Wallet />} />
        <Route path="/me" element={<MemberProfile />} />
        <Route path="/verify-identity" element={<VerifyIdentity />} />
        <Route path="/search" element={<Search />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/love/safety" element={<LoveSafetyHub />} />
        <Route path="/safety-guides" element={<SafetyGuides />} />
        <Route path="/transparency" element={<Transparency />} />
        <Route path="/policies" element={<Policies />} />
        <Route path="/safety-tools" element={<SafetyTools />} />
        <Route path="/dispute-resolution" element={<DisputeResolution />} />
        <Route path="/identity/complete" element={<IdentityComplete />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/business-model" element={<BusinessModel />} />
        <Route path="/pledge" element={<Pledge />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/code-of-conduct" element={<CodeOfConduct />} />
        <Route path="/mavula" element={<Mavula />} />
        <Route path="/why" element={<Why />} />
        <Route path="/vote" element={<Vote />} />
        <Route path="/your-voice" element={<YourVoice />} />
        <Route path="/creators" element={<Creators />} />
        <Route path="/creators/dashboard" element={<CreatorDashboard />} />
        <Route path="/lens" element={<PulaLens />} />
        <Route path="/moments" element={<PulaMoments />} />
        <Route path="/wise" element={<AskTheWise />} />
        <Route path="/wise/:id" element={<WiseQuestion />} />
        <Route path="/legacy-fund" element={<LegacyFund />} />
        <Route path="/lens/about" element={<LensAbout />} />
        <Route path="/get-app" element={<GetApp />} />
        <Route path="/settings/notifications" element={<NotificationSettings />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/chat" element={<PulaChat />} />
        <Route path="/stories/:id" element={<StoryView />} />
        <Route path="/students/market" element={<StudentMarketplace />} />
        <Route path="/students/gigs" element={<StudentGigs />} />
        <Route path="/students/voice" element={<StudentVoice />} />
        <Route path="/students/extras" element={<StudentExtras />} />
        <Route path="/students/store/:id" element={<StudentStore />} />
        <Route path="/students/orders" element={<StudentOrders />} />
        <Route path="/students/groupbuys" element={<StudentGroupBuys />} />
        <Route path="/students/moveout" element={<StudentMoveOut />} />
        <Route path="/students/curiosity" element={<Curiosity />} />
        <Route path="/students/assignments" element={<AssignmentReview />} />
        <Route path="/creators/:id" element={<CreatorProfile />} />
        <Route path="/portfolio/:userId" element={<PublicPortfolio />} />
        <Route path="/readiness/:userId" element={<ReadinessCertificate />} />
        <Route path="/saved" element={<Saved />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/circles" element={<CommunityFinance />} />
        <Route path="/family-circles" element={<FamilyCircle />} />
        <Route path="/family" element={<FamilyHub />} />
        <Route path="/neighbourhood" element={<Neighbourhood />} />
        <Route path="/for-you" element={<ForYou />} />
        <Route path="/business" element={<Business />} />
        <Route path="/pay/:invoiceId" element={<PayInvoice />} />
        <Route path="/students" element={<Students />} />
        <Route path="/for-students" element={<ForStudents />} />
        <Route path="/for-students/leaflet" element={<ForStudentsLeaflet />} />
        <Route path="/community" element={<Community />} />
        <Route path="/connections" element={<Connections />} />
        <Route path="/locate" element={<Locate />} />
        <Route path="/calls" element={<Calls />} />
        <Route path="/taxi" element={<PulaTaxi />} />
        <Route path="/ambassador" element={<Ambassador />} />
        <Route path="/a/:handle" element={<AmbassadorShowcase />} />
        <Route path="/discussion/:id" element={<DiscussionPage />} />
        <Route path="/livelihoods" element={<Livelihoods />} />
        <Route path="/impact" element={<Impact />} />
        <Route path="/about-mavula" element={<AboutMavula />} />
        <Route path="/cv" element={<CVBuilder />} />
        <Route path="/trust" element={<Trust />} />
        <Route path="/how-we-make-money" element={<HowWeMakeMoney />} />
        <Route path="/live-safety" element={<LiveSafetyPage />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/confidante" element={<Confidante />} />
      </Route>

      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/p/:publicId" element={<ProposalView />} />
      <Route path="/launch-ticker" element={<LaunchTicker />} />
      <Route path="/launch-cards" element={<LaunchCards />} />
      <Route path="/launch-feedback" element={<LaunchFeedback />} />
      <Route path="/launch-runsheet" element={<RunSheet />} />
      <Route path="/launch-poster" element={<QrPoster />} />
      <Route path="/launch-table-cards" element={<QrTableTent />} />
      <Route path="/launch-kit" element={<LaunchKit />} />
      <Route path="/launch-rsvp" element={<RsvpLaunch />} />

      <Route path="/portal">
        <Route index element={<PortalLogin />} />
        <Route element={<PortalLayout />}>
          <Route path="exec" element={<ExecOverview />} />
          <Route path="business-plan" element={<BusinessPlan />} />
          <Route path="property-plan" element={<PropertyPlan />} />
          <Route path="live-moderation" element={<LiveModeration />} />
          <Route path="team" element={<Team />} />
          <Route path="referral-leaderboard" element={<ReferralLeaderboard />} />
          <Route path="security" element={<Security />} />
          <Route path="rewards" element={<Rewards />} />
          <Route path="translate" element={<Translate />} />
          <Route path="safety-translations" element={<SafetyTranslations />} />
          <Route path="field" element={<FieldMode />} />
          <Route path="onboarding" element={<Onboarding />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="clients" element={<Clients />} />
          <Route path="cases" element={<Cases />} />
          <Route path="staff" element={<Staff />} />
          <Route path="audit" element={<Audit />} />
          <Route path="resources" element={<Resources />} />
          <Route path="voices" element={<Voices />} />
          <Route path="enquiries" element={<Enquiries />} />
          <Route path="copilot" element={<CopilotStudio />} />
          <Route path="reception" element={<ReceptionLogs />} />
          <Route path="wallet-waitlist" element={<WaitlistDigest />} />
          <Route path="launch-rsvps" element={<LaunchRsvps />} />
          <Route path="staff-activity" element={<StaffActivity />} />
          <Route path="launch-checklist" element={<ChecklistLaunch />} />
          <Route path="launch-recap" element={<RecapLaunch />} />
          <Route path="signup-helper" element={<SignupHelper />} />
          <Route path="verifications" element={<Verifications />} />
          <Route path="discussions" element={<DiscussionsMod />} />
          <Route path="happenings" element={<HappeningsMod />} />
          <Route path="community-mods" element={<CommunityMods />} />
          <Route path="market-reports" element={<MarketReports />} />
          <Route path="market-search" element={<MarketSearchAdmin />} />
          <Route path="maker-verifications" element={<MakerVerifications />} />
          <Route path="local-businesses" element={<LocalBusinessesAdmin />} />
          <Route path="ads" element={<AdsAdmin />} />
          <Route path="safety-concerns" element={<SafetyConcerns />} />
          <Route path="student-reports" element={<StudentReports />} />
          <Route path="student-disputes" element={<StudentDisputes />} />
          <Route path="jobs-moderation" element={<JobsModeration />} />
          <Route path="lens-moderation" element={<LensModeration />} />
          <Route path="legacy-fund" element={<LegacyFundReview />} />
          <Route path="blog" element={<BlogAdmin />} />
          <Route path="analytics" element={<PortalAnalytics />} />
          <Route path="marketing" element={<PortalMarketing />} />
          <Route path="broadcast-channels" element={<PortalBroadcastChannels />} />
          <Route path="reengage" element={<PortalReengage />} />
          <Route path="lead-studio" element={<LeadStudio />} />
          <Route path="ai-team" element={<AITeam />} />
          <Route path="proposals" element={<Proposals />} />
          <Route path="ambassadors" element={<AmbassadorsAdmin />} />
        </Route>
      </Route>
    </Routes>
    </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <SettingsProvider>
      <AuthProvider>
        <NotesProvider>
          <BrowserRouter>
            <PinPromptProvider>
              <CookieConsentProvider>
                <CallProvider>
                  <AnalyticsTracker />
                  <AutoTranslate />
                  <LanguagePrompt />
                  <LanguageHint />
                  <OfflineBanner />
                  <DataSaverPrompt />
                  <AppRoutes />
                  <CallOverlay />
                  <GroupCallOverlay />
                  <CallNotifyPrompt />
                  <WelcomeAlertsStep />
                  <MissedCallBanner />
                  <InstallBanner />
                  <Toaster position="top-center" />
                </CallProvider>
              </CookieConsentProvider>
            </PinPromptProvider>
          </BrowserRouter>
        </NotesProvider>
      </AuthProvider>
    </SettingsProvider>
    </ErrorBoundary>
  );
}

export default App;
